import { Link } from 'react-router-dom';
import { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ChevronLeft, ChevronRight, Shield, AlertTriangle, Heart, Zap, Utensils, 
  MessageCircle, Activity, ClipboardList, Presentation, Bone, FlaskConical, 
  Brain, ThumbsUp, ThumbsDown, ChevronDown, Menu, X, Mail, MapPin
} from 'lucide-react';

/* eslint-disable react/no-unescaped-entities */

// =========================================================
// CAUSES PAGE V1 — Direction "Souffle Organique"
// DESIGN ONLY - All original content preserved exactly
// =========================================================

const C = {
  bg: '#F9F8F6',
  surface: '#F2EEE5',
  ink: '#2C423F',
  ink2: '#5A6B66',
  sage: '#8FA89B',
  sageDeep: '#6B8478',
  terre: '#E6DFD3',
  hairline: '#DDD4C5',
  warmAccent: '#C9483B',
};

const Serif = ({ children, className = '', as: As = 'span', italic = false, ...rest }) => (
  <As
    className={className}
    style={{
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      letterSpacing: '-0.005em',
      fontStyle: italic ? 'italic' : 'normal',
    }}
    {...rest}
  >
    {children}
  </As>
);

const Eyebrow = ({ children }) => (
  <span
    className="inline-flex items-center gap-3 text-xs tracking-[0.25em] uppercase"
    style={{ color: C.sageDeep, fontFamily: 'Figtree, sans-serif' }}
  >
    <span className="w-8 h-px" style={{ backgroundColor: C.sageDeep }} />
    {children}
  </span>
);

const GrainOverlay = () => (
  <div
    aria-hidden
    className="pointer-events-none fixed inset-0 z-[1] opacity-[0.035] mix-blend-multiply"
    style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
    }}
  />
);

const BreathLine = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 1200 60" fill="none" preserveAspectRatio="none" aria-hidden>
    <path
      d="M0 30 Q 150 0, 300 30 T 600 30 T 900 30 T 1200 30"
      stroke={C.sage}
      strokeWidth="1"
      fill="none"
    />
  </svg>
);

// Organic Card Component
const OrganicCard = ({ children, className = '', hover = true, padding = 'p-8', style = {} }) => (
  <div
    className={`rounded-[24px] ${padding} transition-all duration-300 ${hover ? 'hover:-translate-y-1' : ''} ${className}`}
    style={{
      backgroundColor: C.bg,
      border: `1px solid ${C.hairline}`,
      boxShadow: '0 4px 20px -8px rgba(44,66,63,0.08)',
      ...style,
    }}
  >
    {children}
  </div>
);

const CausesPagePreviewV1 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const closeTimeoutRef = useRef(null);

  const handleMouseEnter = (index) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setDropdownOpen(index);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setDropdownOpen(null);
    }, 200);
  };

  const menuItems = [
    { path: '/', label: 'Accueil', type: 'link' },
    {
      label: 'Troubles respiratoires', type: 'dropdown',
      items: [
        { path: '/hyperventilation', label: 'Hyperventilation' },
        { path: '/respiration-dysfonctionnelle', label: 'Respiration Dysfonctionnelle' },
        { path: '/asthme', label: 'Asthme' },
        { path: '/covid-long', label: 'COVID Long' },
      ],
    },
    {
      label: 'Ressources théoriques', type: 'dropdown',
      items: [
        { path: '/causes-troubles-respiratoires', label: 'Causes des Troubles Respiratoires' },
        { path: '/mecanique-respiratoire', label: 'Mécanique Respiratoire' },
        { path: '/respiration-optimale', label: 'Respiration Optimale' },
        { path: '/nerf-vague', label: 'Nerf Vague' },
      ],
    },
    {
      label: 'Techniques', type: 'dropdown',
      items: [
        { path: '/coherence-cardiaque', label: 'Cohérence Cardiaque' },
        { path: '/methode-buteyko', label: 'Méthode Buteyko' },
        { path: '/mobilite-mouvement', label: 'Mobilité et Mouvement' },
        { path: '/entrainement-muscles-respiratoires', label: 'Entraînement des Muscles Respiratoires' },
      ],
    },
    { path: '/evaluer-respiration', label: 'Tester sa respiration', type: 'link' },
    { path: '/documents', label: 'Documents', type: 'link' },
  ];

  const parasympatheticFeatures = [
    {
      icon: Heart,
      title: "Relaxation",
      description: "État de calme et de détente profonde du corps et de l'esprit"
    },
    {
      icon: Utensils,
      title: "Digestion",
      description: "Optimisation de la fonction digestive et de l'absorption des nutriments"
    },
    {
      icon: MessageCircle,
      title: "Interactions Sociales",
      description: "Capacité à communiquer, créer des liens et s'ouvrir aux autres"
    }
  ];

  const sympatheticFeatures = [
    {
      hormone: "Adrénaline",
      effects: [
        "Augmentation de la fréquence cardiaque",
        "Dilatation des bronches",
        "Libération rapide d'énergie",
        "Préparation à l'action immédiate"
      ]
    },
    {
      hormone: "Cortisol",
      effects: [
        "Augmentation de la glycémie",
        "Suppression du système immunitaire",
        "Augmentation de la tension artérielle",
        "Mobilisation des réserves énergétiques"
      ]
    }
  ];

  return (
    <div
      className="min-h-screen relative"
      style={{ backgroundColor: C.bg, color: C.ink, fontFamily: 'Figtree, sans-serif' }}
      data-testid="causes-page-v1"
    >
      <GrainOverlay />

      {/* ============ HEADER ============ */}
      <header
        className="sticky top-0 z-40"
        style={{
          backgroundColor: 'rgba(249,248,246,0.55)',
          backdropFilter: 'saturate(180%) blur(24px)',
          WebkitBackdropFilter: 'saturate(180%) blur(24px)',
          borderBottom: `1px solid rgba(221,212,197,0.5)`,
          boxShadow: '0 1px 30px -10px rgba(44,66,63,0.06)',
        }}
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-3">
              <button onClick={() => setProfileModalOpen(true)} className="group relative">
                <img
                  src="/julien-jaccard.png"
                  alt="Julien Jaccard"
                  className="w-14 h-14 rounded-full object-cover transition-all group-hover:opacity-90"
                  style={{ border: `2px solid ${C.sage}` }}
                />
              </button>
              <button onClick={() => setProfileModalOpen(true)} className="hidden sm:block text-left">
                <Serif as="p" className="text-xl leading-tight" style={{ color: C.ink }}>Julien Jaccard</Serif>
                <p className="text-xs mt-0.5" style={{ color: C.ink2 }}>Physiothérapeute</p>
              </button>
              <img
                src="/mains-nuages-logo.jpg"
                alt="Les Mains Nuages"
                className="hidden md:block w-12 h-12 object-contain ml-2 rounded-lg"
              />
            </div>

            <nav className="hidden lg:flex items-center gap-7">
              {menuItems.map((item, index) => (
                item.type === 'link' ? (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="text-sm transition-colors"
                    style={{ color: location.pathname === item.path ? C.sageDeep : C.ink, fontFamily: 'Figtree, sans-serif' }}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <div key={item.label} className="relative" onMouseEnter={() => handleMouseEnter(index)} onMouseLeave={handleMouseLeave}>
                    <button className="flex items-center gap-1 text-sm transition-colors" style={{ color: C.ink, fontFamily: 'Figtree, sans-serif' }}>
                      {item.label}
                      <ChevronDown size={14} className={`transition-transform ${dropdownOpen === index ? 'rotate-180' : ''}`} />
                    </button>
                    {dropdownOpen === index && (
                      <div className="absolute top-full left-0 pt-3 w-72 z-50">
                        <div
                          className="rounded-2xl py-2 overflow-hidden"
                          style={{
                            backgroundColor: 'rgba(249,248,246,0.85)',
                            backdropFilter: 'saturate(180%) blur(20px)',
                            border: `1px solid rgba(221,212,197,0.6)`,
                            boxShadow: '0 20px 50px -15px rgba(44,66,63,0.18)',
                          }}
                        >
                          {item.items.map((subItem) => (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              onClick={() => setDropdownOpen(null)}
                              className="block px-5 py-2.5 text-sm transition-colors hover:opacity-70"
                              style={{ color: location.pathname === subItem.path ? C.sageDeep : C.ink, fontFamily: 'Figtree, sans-serif' }}
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              ))}
              <button
                onClick={() => navigate('/app-landing')}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all duration-300"
                style={{ backgroundColor: C.sage, color: C.bg, fontFamily: 'Figtree, sans-serif' }}
              >
                Exercices
              </button>
            </nav>

            <button className="lg:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} style={{ color: C.ink }}>
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {mobileMenuOpen && (
            <nav className="lg:hidden py-4" style={{ borderTop: `1px solid ${C.hairline}` }}>
              <div className="flex flex-col gap-1">
                {menuItems.map((item, index) => (
                  item.type === 'link' ? (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className="px-4 py-2.5 rounded-lg text-sm"
                      style={{ color: location.pathname === item.path ? C.sageDeep : C.ink, fontFamily: 'Figtree, sans-serif' }}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <div key={item.label} className="px-4">
                      <button
                        onClick={() => setDropdownOpen(dropdownOpen === index ? null : index)}
                        className="flex items-center justify-between w-full py-2.5 text-sm"
                        style={{ color: C.ink, fontFamily: 'Figtree, sans-serif' }}
                      >
                        {item.label}
                        <ChevronDown size={16} className={`transition-transform ${dropdownOpen === index ? 'rotate-180' : ''}`} />
                      </button>
                      {dropdownOpen === index && (
                        <div className="pl-4 mt-1 space-y-1 pb-2">
                          {item.items.map((subItem) => (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              onClick={() => setMobileMenuOpen(false)}
                              className="block px-3 py-2 rounded-lg text-sm"
                              style={{ color: C.ink2, fontFamily: 'Figtree, sans-serif' }}
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                ))}
                <div className="px-4 mt-3">
                  <button
                    onClick={() => { setMobileMenuOpen(false); navigate('/app-landing'); }}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium"
                    style={{ backgroundColor: C.sage, color: C.bg }}
                  >
                    Exercices
                  </button>
                </div>
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Profile Modal */}
      {profileModalOpen && (
        <div
          className="fixed inset-0 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ zIndex: 9999, backgroundColor: 'rgba(44,66,63,0.55)' }}
          onClick={() => setProfileModalOpen(false)}
        >
          <div
            className="rounded-[28px] max-w-md w-full p-10 relative"
            style={{ backgroundColor: C.bg, border: `1px solid ${C.hairline}` }}
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => setProfileModalOpen(false)} className="absolute top-4 right-4 p-2 rounded-full" style={{ color: C.ink2 }}>
              <X size={20} />
            </button>
            <div className="flex flex-col items-center text-center">
              <img src="/julien-jaccard.png" alt="Julien Jaccard" className="w-44 h-44 rounded-full object-cover mb-6" style={{ border: `3px solid ${C.sage}` }} />
              <Serif as="h2" className="text-3xl mb-2" style={{ color: C.ink }}>Julien Jaccard</Serif>
              <p className="text-base mb-1" style={{ color: C.ink2 }}>Physiothérapeute</p>
              <div className="flex items-center gap-2 mb-7" style={{ color: C.ink2 }}>
                <MapPin size={16} />
                <span className="text-sm">Valais, Suisse</span>
              </div>
              <a
                href="mailto:julienjaccard@mains-nuages.ch"
                className="inline-flex items-center gap-3 px-7 py-4 rounded-full text-sm font-medium"
                style={{ backgroundColor: C.sage, color: C.bg, fontFamily: 'Figtree, sans-serif' }}
              >
                <Mail size={16} />
                julienjaccard@mains-nuages.ch
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ============ HERO SECTION ============ */}
      <section className="relative py-20 overflow-hidden">
        <div className="max-w-[1100px] mx-auto px-8 relative z-10">
          <div className="flex items-center justify-between mb-6">
            <Eyebrow>Comprendre</Eyebrow>
          </div>
          
          <Serif as="h1" className="text-[clamp(2rem,4vw,3.5rem)] leading-[1.1] font-medium">
            Causes des Troubles <Serif italic style={{ color: C.sage }}>Respiratoires</Serif>
          </Serif>
          <p className="mt-8 text-lg leading-relaxed max-w-2xl" style={{ color: C.ink2 }}>
            Comprendre le rôle du système nerveux autonome
          </p>
          
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/evaluer-respiration"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-sm font-medium transition-all duration-300"
              style={{ backgroundColor: C.sage, color: C.bg, fontFamily: 'Figtree, sans-serif' }}
            >
              Évaluer votre respiration
              <ChevronRight size={18} />
            </Link>
          </div>
        </div>

        {/* Decorative blob */}
        <div
          aria-hidden
          className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-30"
          style={{ background: `radial-gradient(circle at 30% 30%, ${C.sage}40, transparent 70%)` }}
        />
      </section>

      <BreathLine className="w-full h-10 opacity-40" />

      {/* ============ INTRODUCTION SECTION ============ */}
      <section className="py-24" style={{ backgroundColor: C.surface }}>
        <div className="max-w-[900px] mx-auto px-8">
          {/* Patient testimonial */}
          <div
            className="rounded-[28px] p-10 mb-12"
            style={{
              backgroundColor: C.bg,
              borderLeft: `4px solid ${C.sage}`,
              boxShadow: '0 4px 20px -8px rgba(44,66,63,0.08)',
            }}
          >
            <Serif as="blockquote" italic className="text-xl md:text-2xl leading-relaxed" style={{ color: C.ink }}>
              "J'éprouve un inconfort respiratoire au quotidien, je suis vite essoufflé à l'effort, voire juste en parlant, ou en mangeant, j'ai l'impression de ne pas arriver à prendre suffisamment d'air"
            </Serif>
          </div>

          <div className="space-y-6" style={{ color: C.ink }}>
            <p className="text-lg leading-relaxed">
              Voilà les paroles des patients que je reçois chaque jour, et une affirmation revient souvent : <Serif italic>"On me dit que je ne sais pas respirer, je respire tout faux"</Serif>...
            </p>

            <p className="text-lg leading-relaxed">
              Eh bien avant tout, il faut comprendre une chose cruciale, <strong style={{ color: C.sageDeep }} className="text-xl">VOUS ne respirez PAS</strong>...
            </p>

            <p className="text-lg leading-relaxed">
              Non, ce n'est pas vous qui respirez, c'est le corps qui respire, et bien heureusement, il fait ça tout seul, comme un grand, comme de faire battre le cœur, de gérer la digestion, de permettre la perception d'images, de sons, de sensations, d'initier un mouvement, de dormir ou d'aller aux toilettes... En fait, <strong style={{ color: C.sageDeep }}>VOUS ne faites RIEN !</strong>
            </p>

            <p className="text-lg leading-relaxed">
              Le corps est une machine à vivre, le résultat de millions d'années d'évolution, une machine dont la compétence principale est la capacité à s'auto-entretenir, résister aux agressions du monde extérieur et intérieur, s'auto-réparer, cicatriser, trouver continuellement les ressources pour maintenir ou retrouver son équilibre, ce que l'on appelle l'<strong style={{ color: C.sageDeep }}>homéostasie</strong>.
            </p>

            <p className="text-lg leading-relaxed">
              Lorsque vous sentez votre cœur battre plus vite, plus fort, ça n'est pas un problème, c'est une <Serif italic>solution</Serif> que le cœur trouve pour maintenir l'apport sanguin alors que celui-ci est menacé.
            </p>

            <p className="text-lg leading-relaxed">
              Lorsque tout d'un coup vos épaules se contractent, que la digestion se bloque, que vous n'arrivez plus à dormir, que votre respiration s'accélère, <strong style={{ color: C.ink }}>ce ne sont pas des problèmes</strong>.
            </p>

            <p className="text-lg leading-relaxed">
              Ce sont des expressions ressenties d'un corps qui se sent en danger, et qui se prépare à une agression, qu'elle soit réelle ou... simplement imaginée.
            </p>
          </div>

          {/* Key insight box */}
          <OrganicCard className="mt-12 text-center" hover={false} padding="p-10">
            <Serif as="p" className="text-xl md:text-2xl leading-relaxed" style={{ color: C.ink }}>
              Ce n'est donc pas vous qui ne savez pas respirer, ce n'est pas non plus le corps qui ne sait pas respirer,
              <br />
              <Serif as="span" className="text-2xl md:text-3xl mt-4 block" style={{ color: C.sageDeep }}>
                C'est <strong>NOUS</strong> qui ne savons pas <strong>ÉCOUTER</strong> !
              </Serif>
            </Serif>
          </OrganicCard>

          <div className="space-y-6 mt-12" style={{ color: C.ink }}>
            <p className="text-lg leading-relaxed">
              Écouter les messages du corps en comprenant que celui-ci est toujours juste. Écouter ses réactions, comprendre quand le corps se sent en sécurité, quand il se sent menacé, et ensuite, se poser la question : <strong style={{ color: C.sageDeep }}>Par Quoi ?</strong>
            </p>

            <p className="text-lg leading-relaxed">
              On en revient alors à l'identification des <strong>3 types de stresseurs</strong> qui tentent de déstabiliser l'organisme et le forcent à s'adapter :
            </p>
          </div>

          {/* 3 Stressors */}
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            <OrganicCard className="text-center" padding="p-8">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: C.surface }}>
                <Bone size={24} style={{ color: C.warmAccent }} />
              </div>
              <Serif as="h3" className="text-xl" style={{ color: C.ink }}>1. Mécaniques</Serif>
            </OrganicCard>
            <OrganicCard className="text-center" padding="p-8">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: C.surface }}>
                <FlaskConical size={24} style={{ color: C.sage }} />
              </div>
              <Serif as="h3" className="text-xl" style={{ color: C.ink }}>2. Chimiques</Serif>
            </OrganicCard>
            <OrganicCard className="text-center" padding="p-8">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: C.surface }}>
                <Brain size={24} style={{ color: C.sageDeep }} />
              </div>
              <Serif as="h3" className="text-xl" style={{ color: C.ink }}>3. Psychologiques</Serif>
            </OrganicCard>
          </div>

          <div className="space-y-6 mt-12" style={{ color: C.ink }}>
            <p className="text-lg leading-relaxed">
              Voici le cœur de notre pratique, pour aider le corps à retrouver cet état de relâchement, où la respiration est calme, diaphragmatique, silencieuse et douce, où le cœur bat tranquillement, où la digestion se fait sans bruit, où le sommeil est facile, profond, réparateur...
            </p>

            <p className="text-lg leading-relaxed">
              Non ce n'est pas impossible, non ce n'est pas si difficile, si vous comprenez l'intelligence naturelle du corps, et que vous l'écoutez, reconnaissez ses plaintes, et comprenez que, comme votre voiture, si vous l'entretenez, si vous lui donnez les nutriments qui lui conviennent, si vous n'appuyez pas continuellement sur l'accélérateur sans jamais utiliser le frein...
            </p>

            <p className="text-lg leading-relaxed">
              Si vous l'utilisez conformément au manuel d'utilisation que nous n'avons jamais reçu mais qui pourtant existe, était connu par les sociétés anciennes et encore aujourd'hui par les tribus qui n'ont pas été polluées par l'arrogance de la civilisation moderne, qui ne sait même plus comment manger, comment dormir, comment faire naître un enfant ni même s'en occuper, qui a perdu le bon sens inné de tout organisme vivant.
            </p>

            <p className="text-lg leading-relaxed">
              Les animaux se posent-ils la question de savoir quelle nourriture leur convient ? À quel moment se reproduire ? Combien de fois par jour il faudrait aller aux toilettes ?
            </p>
          </div>

          {/* Final message */}
          <div
            className="rounded-[28px] p-10 mt-12"
            style={{
              backgroundColor: C.bg,
              borderLeft: `4px solid ${C.sageDeep}`,
              boxShadow: '0 4px 20px -8px rgba(44,66,63,0.08)',
            }}
          >
            <p className="text-lg leading-relaxed" style={{ color: C.ink }}>
              La proposition ici est un <strong style={{ color: C.sageDeep }}>retour à l'écoute</strong>, à la saveur de ressentir cette expérience de vie que nous permettent les sens, un retour au seul moment réel, <strong style={{ color: C.sageDeep }}>CE moment</strong>, cet éternel maintenant... Et de permettre au corps de revenir à son état de base, un état de détente, de bien-être, <Serif italic>le seul état dans lequel le corps peut faire son travail</Serif>.
            </p>
          </div>
        </div>
      </section>

      {/* ============ DOWNLOAD BUTTONS ============ */}
      <section className="py-16">
        <div className="max-w-[900px] mx-auto px-8">
          <div className="flex flex-wrap gap-4 justify-center">
            <a 
              href="/documents/Questionnaire_SNA.xlsx" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-6 py-4 rounded-full text-sm font-medium transition-all duration-300 hover:-translate-y-1"
              style={{ backgroundColor: C.sageDeep, color: C.bg, fontFamily: 'Figtree, sans-serif' }}
            >
              <ClipboardList size={20} />
              Évaluer son système nerveux autonome
            </a>
            <a 
              href="/documents/Causes_Troubles_Respiratoires.pptx"
              download
              className="inline-flex items-center gap-3 px-6 py-4 rounded-full text-sm font-medium transition-all duration-300 hover:-translate-y-1"
              style={{ backgroundColor: C.warmAccent, color: C.bg, fontFamily: 'Figtree, sans-serif' }}
            >
              <Presentation size={20} />
              Powerpoint complet sur les causes
            </a>
          </div>
        </div>
      </section>

      <BreathLine className="w-full h-10 opacity-40" />

      {/* ============ BODY'S ADAPTATION & STRESSORS ============ */}
      <section className="py-24">
        <div className="max-w-[1100px] mx-auto px-8">
          {/* Introduction */}
          <div className="text-center mb-16">
            <Eyebrow>Adaptation</Eyebrow>
            <Serif as="h2" className="mt-6 text-4xl md:text-5xl leading-tight">
              La Capacité d'Adaptation <Serif italic style={{ color: C.sage }}>du Corps</Serif>
            </Serif>
            <p className="mt-6 text-lg max-w-3xl mx-auto" style={{ color: C.ink2 }}>
              Le corps possède une <strong style={{ color: C.sageDeep }}>formidable capacité de s'adapter</strong> et de maintenir ou restaurer la santé. Mais s'adapter à quoi ?
            </p>
          </div>

          {/* Stressors Explanation */}
          <OrganicCard className="mb-12" hover={false} padding="p-10">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: C.surface }}>
                <Zap className="w-7 h-7" style={{ color: C.warmAccent }} />
              </div>
              <div>
                <Serif as="h3" className="text-2xl mb-2" style={{ color: C.ink }}>Les Stresseurs</Serif>
                <p className="text-lg" style={{ color: C.ink2 }}>
                  Le corps est constamment soumis à des forces qui tendent à perturber son équilibre. On les appelle des <strong style={{ color: C.warmAccent }}>stresseurs</strong>.
                </p>
              </div>
            </div>
            <p className="text-lg font-medium text-center" style={{ color: C.ink2 }}>
              Ces stresseurs se divisent en <strong>3 catégories principales</strong> :
            </p>
          </OrganicCard>

          {/* Three Types of Stressors - Detailed */}
          <div className="grid md:grid-cols-3 gap-6">
            {/* Traumatic */}
            <OrganicCard padding="p-6">
              <div className="h-1 rounded-full mb-6" style={{ backgroundColor: C.warmAccent }} />
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: C.surface }}>
                <Bone className="w-8 h-8" style={{ color: C.warmAccent }} />
              </div>
              <Serif as="h4" className="text-xl text-center mb-3" style={{ color: C.ink }}>Traumatiques</Serif>
              <p className="text-sm text-center mb-4" style={{ color: C.ink2 }}>
                Agressions physiques sur le corps
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm" style={{ color: C.ink2 }}>
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: C.warmAccent }}></span>
                  Accidents
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: C.ink2 }}>
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: C.warmAccent }}></span>
                  Fractures
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: C.ink2 }}>
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: C.warmAccent }}></span>
                  Opérations chirurgicales
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: C.ink2 }}>
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: C.warmAccent }}></span>
                  Cicatrices
                </li>
              </ul>
            </OrganicCard>

            {/* Chemical */}
            <OrganicCard padding="p-6">
              <div className="h-1 rounded-full mb-6" style={{ backgroundColor: C.sageDeep }} />
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: C.surface }}>
                <FlaskConical className="w-8 h-8" style={{ color: C.sageDeep }} />
              </div>
              <Serif as="h4" className="text-xl text-center mb-3" style={{ color: C.ink }}>Chimiques</Serif>
              <p className="text-sm text-center mb-4" style={{ color: C.ink2 }}>
                Substances affectant l'organisme
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm" style={{ color: C.ink2 }}>
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: C.sageDeep }}></span>
                  Alimentation inadaptée
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: C.ink2 }}>
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: C.sageDeep }}></span>
                  Toxiques environnementaux
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: C.ink2 }}>
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: C.sageDeep }}></span>
                  Médicaments
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: C.ink2 }}>
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: C.sageDeep }}></span>
                  Polluants
                </li>
              </ul>
            </OrganicCard>

            {/* Psychological */}
            <OrganicCard padding="p-6">
              <div className="h-1 rounded-full mb-6" style={{ backgroundColor: C.sage }} />
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: C.surface }}>
                <Brain className="w-8 h-8" style={{ color: C.sage }} />
              </div>
              <Serif as="h4" className="text-xl text-center mb-3" style={{ color: C.ink }}>Psychologiques</Serif>
              <p className="text-sm text-center mb-4" style={{ color: C.ink2 }}>
                La puissance de nos pensées
              </p>
              <div className="space-y-3">
                <div className="p-3 rounded-xl" style={{ backgroundColor: C.surface }}>
                  <div className="flex items-center gap-2 mb-1">
                    <ThumbsUp className="w-4 h-4" style={{ color: C.sage }} />
                    <span className="text-sm font-bold" style={{ color: C.sageDeep }}>Pensées Positives</span>
                  </div>
                  <p className="text-xs" style={{ color: C.ink2 }}>
                    → Activation du système de relaxation
                  </p>
                </div>
                <div className="p-3 rounded-xl" style={{ backgroundColor: C.surface }}>
                  <div className="flex items-center gap-2 mb-1">
                    <ThumbsDown className="w-4 h-4" style={{ color: C.warmAccent }} />
                    <span className="text-sm font-bold" style={{ color: C.warmAccent }}>Pensées Négatives</span>
                  </div>
                  <p className="text-xs" style={{ color: C.ink2 }}>
                    Peur, colère, frustration → Activation du système de stress
                  </p>
                </div>
              </div>
            </OrganicCard>
          </div>

          {/* Key Message */}
          <div className="mt-12 p-8 rounded-[28px] text-center" style={{ backgroundColor: C.ink, color: C.bg }}>
            <p className="text-lg font-medium">
              <strong>Le corps écoute nos pensées en permanence.</strong> Leur caractère positif ou négatif influence directement l'activation de notre système nerveux autonome.
            </p>
          </div>
        </div>
      </section>

      <BreathLine className="w-full h-10 opacity-40" />

      {/* ============ THE BODY AS A LIFE-MAINTAINING MACHINE ============ */}
      <section className="py-24" style={{ backgroundColor: C.surface }}>
        <div className="max-w-[900px] mx-auto px-8">
          <OrganicCard className="mb-12" hover={false} padding="p-10">
            <div className="flex items-start gap-4">
              <Shield className="w-12 h-12 flex-shrink-0 mt-1" style={{ color: C.sage }} />
              <div>
                <Serif as="h2" className="text-2xl mb-3" style={{ color: C.ink }}>Le Corps : Une Merveilleuse Machine à Maintenir la Vie</Serif>
                <p className="text-lg" style={{ color: C.ink2 }}>
                  Notre corps est une machine extraordinaire dont l'objectif premier est de <strong style={{ color: C.sageDeep }}>maintenir la vie à tout prix</strong>. Pour accomplir cette mission, il dispose d'un mécanisme primordial qui fonctionne 24 heures sur 24, 7 jours sur 7, sans que nous en ayons conscience : <strong style={{ color: C.sageDeep }}>le système nerveux autonome</strong>.
                </p>
              </div>
            </div>
          </OrganicCard>

          <div className="space-y-6" style={{ color: C.ink }}>
            <p className="text-lg leading-relaxed">
              Ce système agit comme un <strong style={{ color: C.sageDeep }}>gardien vigilant</strong> qui scanne continuellement votre environnement extérieur (ce qui se passe autour de vous) et intérieur (ce qui se passe dans votre corps), à la recherche de signes de danger pour l'intégrité du corps et le maintien de la vie.
            </p>

            <p className="text-lg leading-relaxed">
              Ce processus s'appelle la <strong style={{ color: C.sageDeep }}>neuroception</strong> : une évaluation inconsciente et automatique du niveau de sécurité ou d'insécurité de notre environnement, effectuée par notre système nerveux sans que notre cerveau conscient n'ait à intervenir.
            </p>
          </div>
        </div>
      </section>

      {/* ============ TWO MODES ============ */}
      <section className="py-24">
        <div className="max-w-[1100px] mx-auto px-8">
          <div className="text-center mb-16">
            <Eyebrow>Système Nerveux</Eyebrow>
            <Serif as="h2" className="mt-6 text-4xl md:text-5xl leading-tight">
              Deux Modes <Serif italic style={{ color: C.sage }}>Complètement Opposés</Serif>
            </Serif>
            <p className="mt-6 text-lg max-w-2xl mx-auto" style={{ color: C.ink2 }}>
              En fonction de la réponse à cette évaluation continue — <strong>"Suis-je en sécurité ?"</strong> ou <strong>"Suis-je en insécurité ?"</strong> — le système nerveux active l'un de ces deux modes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Parasympathetic Mode - Safety */}
            <OrganicCard hover={false} padding="p-10">
              <div className="h-1 rounded-full mb-8" style={{ backgroundColor: C.sage }} />
              <div className="flex items-center gap-4 mb-8">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: C.surface }}>
                  <Shield size={28} style={{ color: C.sage }} />
                </div>
                <div>
                  <span className="text-xs font-medium uppercase tracking-wider" style={{ color: C.sage }}>Réponse</span>
                  <Serif as="h3" className="text-2xl" style={{ color: C.ink }}>SÉCURITÉ</Serif>
                </div>
              </div>

              <div className="inline-block px-4 py-2 rounded-full mb-6" style={{ backgroundColor: C.surface }}>
                <span className="text-sm font-medium" style={{ color: C.sage }}>Mode Parasympathique</span>
              </div>

              <p className="text-sm mb-6" style={{ color: C.ink2 }}>
                Lorsque le système nerveux détecte la sécurité, il active le <strong>système parasympathique</strong>, également appelé système de "repos et digestion" ou "restauration".
              </p>

              <div className="space-y-3">
                <h4 className="text-xs font-medium uppercase tracking-wider mb-4" style={{ color: C.ink }}>Fonctions Activées</h4>
                {parasympatheticFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: C.surface }}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: C.bg }}>
                      <feature.icon size={16} style={{ color: C.sage }} />
                    </div>
                    <div>
                      <div className="font-medium text-sm" style={{ color: C.ink }}>{feature.title}</div>
                      <div className="text-xs" style={{ color: C.ink2 }}>{feature.description}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 rounded-xl" style={{ backgroundColor: C.surface, borderLeft: `3px solid ${C.sage}` }}>
                <p className="text-sm" style={{ color: C.ink }}>
                  <strong>État optimal :</strong> C'est dans cet état que le corps se régénère, guérit, digère correctement et que nous pouvons vivre des relations épanouissantes.
                </p>
              </div>
            </OrganicCard>

            {/* Sympathetic Mode - Danger */}
            <OrganicCard hover={false} padding="p-10">
              <div className="h-1 rounded-full mb-8" style={{ backgroundColor: C.warmAccent }} />
              <div className="flex items-center gap-4 mb-8">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: C.surface }}>
                  <AlertTriangle size={28} style={{ color: C.warmAccent }} />
                </div>
                <div>
                  <span className="text-xs font-medium uppercase tracking-wider" style={{ color: C.warmAccent }}>Réponse</span>
                  <Serif as="h3" className="text-2xl" style={{ color: C.ink }}>INSÉCURITÉ</Serif>
                </div>
              </div>

              <div className="inline-block px-4 py-2 rounded-full mb-6" style={{ backgroundColor: C.surface }}>
                <span className="text-sm font-medium" style={{ color: C.warmAccent }}>Mode Sympathique</span>
              </div>

              <p className="text-sm mb-6" style={{ color: C.ink2 }}>
                Lorsque le système nerveux détecte un danger (réel ou perçu), il active le <strong>système sympathique</strong>, également appelé système de "combat ou fuite".
              </p>

              <div className="space-y-4">
                <h4 className="text-xs font-medium uppercase tracking-wider mb-4" style={{ color: C.ink }}>Hormones Libérées</h4>
                {sympatheticFeatures.map((feature, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Zap size={16} style={{ color: C.warmAccent }} />
                      <span className="font-medium" style={{ color: C.ink }}>{feature.hormone}</span>
                    </div>
                    <div className="pl-6 space-y-1">
                      {feature.effects.map((effect, i) => (
                        <div key={i} className="text-xs flex items-start gap-2" style={{ color: C.ink2 }}>
                          <span style={{ color: C.warmAccent }}>•</span>
                          <span>{effect}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 rounded-xl" style={{ backgroundColor: C.surface, borderLeft: `3px solid ${C.warmAccent}` }}>
                <p className="text-sm" style={{ color: C.ink }}>
                  <strong>État de survie :</strong> Conçu pour être temporaire, ce mode nous prépare à réagir face au danger. Problème : il peut devenir chronique.
                </p>
              </div>
            </OrganicCard>
          </div>
        </div>
      </section>

      <BreathLine className="w-full h-10 opacity-40" />

      {/* ============ POLYVAGAL THEORY ============ */}
      <section className="py-24" style={{ backgroundColor: C.surface }}>
        <div className="max-w-[1100px] mx-auto px-8">
          <div className="mb-16">
            <Eyebrow>Théorie</Eyebrow>
            <Serif as="h2" className="mt-6 text-4xl md:text-5xl leading-tight">
              La Théorie Polyvagale <Serif italic style={{ color: C.sage }}>du Dr. Stephen Porges</Serif>
            </Serif>
          </div>

          <OrganicCard className="mb-12" hover={false} padding="p-10" style={{ borderLeft: `4px solid ${C.sageDeep}` }}>
            <Serif as="h3" className="text-xl mb-4" style={{ color: C.ink }}>Une Révolution dans la Compréhension du Stress</Serif>
            <p className="text-lg" style={{ color: C.ink2 }}>
              Le Dr. Stephen Porges, neuroscientifique américain, a développé dans les années 1990 la <strong style={{ color: C.sageDeep }}>théorie polyvagale</strong>, qui révolutionne notre compréhension du système nerveux autonome. Cette théorie démontre que le nerf vague (le principal nerf du système parasympathique) possède en réalité <strong style={{ color: C.sageDeep }}>deux branches distinctes</strong> qui régulent des réponses physiologiques et comportementales très différentes.
            </p>
          </OrganicCard>

          <Serif as="h3" className="text-2xl mb-6" style={{ color: C.ink }}>Les Trois États du Système Nerveux Autonome</Serif>

          <p className="text-lg mb-8" style={{ color: C.ink2 }}>
            Selon la théorie polyvagale, nous ne basculons pas simplement entre deux modes (sympathique et parasympathique), mais entre <strong style={{ color: C.sageDeep }}>trois états hiérarchiques</strong> qui se sont développés au cours de l'évolution :
          </p>

          <div className="space-y-6">
            {/* State 1 */}
            <OrganicCard hover={false} padding="p-8">
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: C.sage }}>
                  <span className="text-xl font-bold" style={{ color: C.bg }}>1</span>
                </div>
                <div className="flex-1">
                  <Serif as="h4" className="text-xl mb-3" style={{ color: C.ink }}>Nerf Vague Ventral (Sécurité & Connexion Sociale)</Serif>
                  <p className="mb-4" style={{ color: C.ink2 }}>
                    État de <strong style={{ color: C.sageDeep }}>sécurité perçue</strong>. Le nerf vague ventral permet la relaxation, la connexion sociale, la communication verbale et faciale. C'est l'état optimal de fonctionnement.
                  </p>
                  <div className="p-4 rounded-xl" style={{ backgroundColor: C.surface }}>
                    <p className="text-sm font-medium mb-1" style={{ color: C.sage }}>Respiration :</p>
                    <p className="text-sm" style={{ color: C.ink2 }}>Lente, profonde, diaphragmatique, régulière. Variation naturelle du rythme (cohérence cardiaque).</p>
                  </div>
                </div>
              </div>
            </OrganicCard>

            {/* State 2 */}
            <OrganicCard hover={false} padding="p-8">
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: C.warmAccent }}>
                  <span className="text-xl font-bold" style={{ color: C.bg }}>2</span>
                </div>
                <div className="flex-1">
                  <Serif as="h4" className="text-xl mb-3" style={{ color: C.ink }}>Système Sympathique (Mobilisation : Fuite ou Combat)</Serif>
                  <p className="mb-4" style={{ color: C.ink2 }}>
                    État d'<strong style={{ color: C.warmAccent }}>insécurité perçue</strong>. Activation du système sympathique pour préparer le corps à fuir ou combattre. Libération d'adrénaline et de cortisol.
                  </p>
                  <div className="p-4 rounded-xl" style={{ backgroundColor: C.surface }}>
                    <p className="text-sm font-medium mb-1" style={{ color: C.warmAccent }}>Respiration :</p>
                    <p className="text-sm" style={{ color: C.ink2 }}><strong>Superficielle, haute fréquence (15-25 cycles/min), petits volumes</strong>. Respiration thoracique haute avec tensions dans le cou et les épaules. Hyperventilation chronique.</p>
                  </div>
                </div>
              </div>
            </OrganicCard>

            {/* State 3 */}
            <OrganicCard hover={false} padding="p-8">
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: C.ink }}>
                  <span className="text-xl font-bold" style={{ color: C.bg }}>3</span>
                </div>
                <div className="flex-1">
                  <Serif as="h4" className="text-xl mb-3" style={{ color: C.ink }}>Nerf Vague Dorsal (Immobilisation : Figement)</Serif>
                  <p className="mb-4" style={{ color: C.ink2 }}>
                    État de <strong style={{ color: C.ink }}>danger extrême perçu</strong> (vie menacée). Lorsque ni la fuite ni le combat ne sont possibles, le système nerveux active le mode le plus ancien : l'immobilisation ou "figement" (freeze). État de dissociation, d'engourdissement émotionnel.
                  </p>
                  <div className="p-4 rounded-xl" style={{ backgroundColor: C.surface }}>
                    <p className="text-sm font-medium mb-1" style={{ color: C.ink }}>Respiration :</p>
                    <p className="text-sm" style={{ color: C.ink2 }}><strong>Respiration irrégulière à grands volumes, avec soupirs et bâillements fréquents</strong>. Peut aussi se manifester par des <strong>apnées inconscientes durant la journée</strong> (retenir sa respiration sans s'en rendre compte). Alternance entre respiration bloquée et grandes inspirations.</p>
                  </div>
                </div>
              </div>
            </OrganicCard>
          </div>

          {/* Danger Warning Box */}
          <OrganicCard className="mt-12" hover={false} padding="p-8" style={{ borderLeft: `4px solid ${C.warmAccent}` }}>
            <Serif as="h3" className="font-bold mb-4 text-lg" style={{ color: C.warmAccent }}> Danger Réel vs Danger Imaginé</Serif>
            <div className="space-y-4" style={{ color: C.ink }}>
              <p className="font-semibold">
                Notre cerveau ne fait PAS la différence entre un danger réel et un danger imaginé par l'esprit.
              </p>
              <p>
                Le système nerveux réagit de la même manière face à :
              </p>
              <ul className="ml-6 space-y-2 text-sm">
                <li>• Un danger physique immédiat (ex: un chien qui attaque)</li>
                <li>• Une situation stressante (ex: présentation au travail, conflit)</li>
                <li>• Une pensée anxieuse (ex: rumination sur un événement passé ou futur)</li>
                <li>• Un souvenir traumatique qui ressurgit</li>
              </ul>
              <p className="mt-4 font-semibold">
                Les <strong style={{ color: C.warmAccent }}>traumatismes qui se racontent dans notre subconscient</strong> ont un impact majeur sur l'activation chronique de notre système sympathique (ou dorsal vagal en cas de trauma sévère).
              </p>
              <p>
                Même si consciemment nous sommes "en sécurité", notre système nerveux peut rester bloqué dans un état de menace si des mémoires traumatiques non résolues continuent d'envoyer des signaux d'alerte au cerveau. Ces traumatismes peuvent être :
              </p>
              <ul className="ml-6 space-y-1 text-sm">
                <li>• Événements traumatiques majeurs (accidents, violences, abus)</li>
                <li>• Traumatismes développementaux (négligence émotionnelle, attachement insécure)</li>
                <li>• Stress chronique accumulé (épuisement professionnel, deuils non résolus)</li>
                <li>• Traumatismes transgénérationnels (transmis de génération en génération)</li>
              </ul>
            </div>
          </OrganicCard>

          {/* Respiratory Expressions */}
          <Serif as="h3" className="text-2xl mt-16 mb-6" style={{ color: C.ink }}>Expressions Respiratoires Spécifiques selon le Mode de Survie</Serif>

          <p className="text-lg mb-8" style={{ color: C.ink2 }}>
            En fonction de notre fonctionnement individuel et de notre histoire traumatique, notre système nerveux peut privilégier un mode de survie particulier, aboutissant à une <strong style={{ color: C.sageDeep }}>expression respiratoire spécifique</strong> :
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Mode Fuite/Combat */}
            <OrganicCard padding="p-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: C.warmAccent }}>
                <Zap className="w-6 h-6" style={{ color: C.bg }} />
              </div>
              <Serif as="h4" className="font-bold mb-3" style={{ color: C.ink }}>Mode Fuite/Combat</Serif>
              <p className="text-sm mb-3" style={{ color: C.ink2 }}>
                <strong>Pattern :</strong> Sympathique dominant
              </p>
              <div className="p-3 rounded-xl" style={{ backgroundColor: C.surface }}>
                <p className="font-semibold text-sm mb-2" style={{ color: C.warmAccent }}>Respiration typique :</p>
                <ul className="space-y-1 ml-4 text-sm" style={{ color: C.ink2 }}>
                  <li>• Superficielle</li>
                  <li>• Haute fréquence (15-25/min)</li>
                  <li>• Petits volumes</li>
                  <li>• Thoracique haute</li>
                  <li>• Tensions cou/épaules</li>
                </ul>
              </div>
            </OrganicCard>

            {/* Mode Figement */}
            <OrganicCard padding="p-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: C.ink }}>
                <AlertTriangle className="w-6 h-6" style={{ color: C.bg }} />
              </div>
              <Serif as="h4" className="font-bold mb-3" style={{ color: C.ink }}>Mode Figement</Serif>
              <p className="text-sm mb-3" style={{ color: C.ink2 }}>
                <strong>Pattern :</strong> Dorsal vagal dominant
              </p>
              <div className="p-3 rounded-xl" style={{ backgroundColor: C.surface }}>
                <p className="font-semibold text-sm mb-2" style={{ color: C.ink }}>Respiration typique :</p>
                <ul className="space-y-1 ml-4 text-sm" style={{ color: C.ink2 }}>
                  <li>• Grands volumes</li>
                  <li>• Soupirs fréquents</li>
                  <li>• Bâillements répétés</li>
                  <li>• Apnées inconscientes</li>
                  <li>• Respiration retenue</li>
                </ul>
              </div>
            </OrganicCard>

            {/* Mode Mixte */}
            <OrganicCard padding="p-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: C.sageDeep }}>
                <Activity className="w-6 h-6" style={{ color: C.bg }} />
              </div>
              <Serif as="h4" className="font-bold mb-3" style={{ color: C.ink }}>Mode Mixte</Serif>
              <p className="text-sm mb-3" style={{ color: C.ink2 }}>
                <strong>Pattern :</strong> Alternance
              </p>
              <div className="p-3 rounded-xl" style={{ backgroundColor: C.surface }}>
                <p className="font-semibold text-sm mb-2" style={{ color: C.sageDeep }}>Respiration typique :</p>
                <ul className="space-y-1 ml-4 text-sm" style={{ color: C.ink2 }}>
                  <li>• Oscillation entre les 2</li>
                  <li>• Hyperventilation puis apnée</li>
                  <li>• Instabilité respiratoire</li>
                  <li>• Difficulté à réguler</li>
                  <li>• Symptômes variables</li>
                </ul>
              </div>
            </OrganicCard>
          </div>

          {/* Understanding Box */}
          <div className="mt-12 p-10 rounded-[28px] text-center" style={{ backgroundColor: C.ink, color: C.bg }}>
            <Serif as="h3" className="text-xl mb-4"> Comprendre pour Guérir</Serif>
            <p style={{ color: 'rgba(249,248,246,0.8)' }}>
              La théorie polyvagale nous montre que les troubles respiratoires ne sont pas "dans la tête", mais des <strong style={{ color: C.sage }}>adaptations inconscientes du système nerveux</strong> face à des menaces perçues. La rééducation respiratoire doit donc viser à <strong style={{ color: C.sage }}>rétablir la sensation de sécurité</strong> dans le système nerveux, en plus de corriger les patterns respiratoires.
            </p>
          </div>
        </div>
      </section>

      {/* ============ THE PROBLEM - CHRONIC ACTIVATION ============ */}
      <section className="py-24">
        <div className="max-w-[900px] mx-auto px-8">
          <Eyebrow>Le Problème</Eyebrow>
          <Serif as="h2" className="mt-6 text-4xl md:text-5xl leading-tight mb-12">
            L'Activation Chronique <Serif italic style={{ color: C.warmAccent }}>du Mode Sympathique</Serif>
          </Serif>

          <OrganicCard className="mb-12" hover={false} padding="p-8" style={{ borderLeft: `4px solid ${C.warmAccent}` }}>
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 flex-shrink-0 mt-1" style={{ color: C.warmAccent }} />
              <div>
                <Serif as="h3" className="font-bold mb-3" style={{ color: C.warmAccent }}>Le Cercle Vicieux</Serif>
                <p style={{ color: C.ink }}>
                  Dans notre société moderne, le système nerveux est constamment bombardé de stimuli qu'il interprète comme des menaces : stress au travail, problèmes relationnels, inquiétudes financières, informations anxiogènes, traumatismes non résolus, manque de sommeil, inflammation chronique...
                </p>
              </div>
            </div>
          </OrganicCard>

          <p className="text-lg mb-8" style={{ color: C.ink }}>
            Lorsque le mode sympathique reste activé de façon <strong style={{ color: C.sageDeep }}>chronique</strong>, le corps reste en permanence en état d'alerte. Cette activation prolongée a des conséquences profondes sur l'ensemble de l'organisme, et notamment sur la respiration.
          </p>

          <Serif as="h3" className="text-2xl mb-6" style={{ color: C.ink }}>Impact sur la Respiration</Serif>

          <div className="grid md:grid-cols-2 gap-6">
            <OrganicCard padding="p-6">
              <Serif as="h4" className="font-bold mb-4" style={{ color: C.ink }}>Modifications Physiologiques</Serif>
              <ul className="space-y-3 text-sm">
                <li className="flex gap-2">
                  <Activity className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: C.warmAccent }} />
                  <span style={{ color: C.ink2 }}><strong style={{ color: C.ink }}>Augmentation de la fréquence respiratoire</strong> pour fournir plus d'oxygène</span>
                </li>
                <li className="flex gap-2">
                  <Activity className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: C.warmAccent }} />
                  <span style={{ color: C.ink2 }}><strong style={{ color: C.ink }}>Respiration thoracique haute</strong> au lieu de diaphragmatique</span>
                </li>
                <li className="flex gap-2">
                  <Activity className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: C.warmAccent }} />
                  <span style={{ color: C.ink2 }}><strong style={{ color: C.ink }}>Tensions musculaires</strong> dans le cou, les épaules, le thorax</span>
                </li>
                <li className="flex gap-2">
                  <Activity className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: C.warmAccent }} />
                  <span style={{ color: C.ink2 }}><strong style={{ color: C.ink }}>Hyperventilation chronique</strong> avec perte excessive de CO₂</span>
                </li>
              </ul>
            </OrganicCard>

            <OrganicCard padding="p-6">
              <Serif as="h4" className="font-bold mb-4" style={{ color: C.ink }}>Conséquences Respiratoires</Serif>
              <ul className="space-y-3 text-sm">
                <li className="flex gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: C.warmAccent }} />
                  <span style={{ color: C.ink2 }}>Syndrome d'hyperventilation chronique</span>
                </li>
                <li className="flex gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: C.warmAccent }} />
                  <span style={{ color: C.ink2 }}>Respiration dysfonctionnelle</span>
                </li>
                <li className="flex gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: C.warmAccent }} />
                  <span style={{ color: C.ink2 }}>Sensation d'oppression thoracique</span>
                </li>
                <li className="flex gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: C.warmAccent }} />
                  <span style={{ color: C.ink2 }}>Soupirs fréquents et bâillements</span>
                </li>
                <li className="flex gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: C.warmAccent }} />
                  <span style={{ color: C.ink2 }}>Sensation de manque d'air paradoxale</span>
                </li>
              </ul>
            </OrganicCard>
          </div>

          <div className="mt-12 p-10 rounded-[28px] text-center" style={{ backgroundColor: C.ink, color: C.bg }}>
            <Serif as="h3" className="text-2xl mb-4">La Clé de la Rééducation Respiratoire</Serif>
            <p style={{ color: 'rgba(249,248,246,0.8)' }} className="text-lg">
              Comprendre que les troubles respiratoires ne sont souvent que le <strong style={{ color: C.sage }}>symptôme visible</strong> d'un système nerveux autonome déséquilibré. La rééducation ne consiste pas seulement à "mieux respirer", mais à <strong style={{ color: C.sage }}>ré-entraîner le système nerveux</strong> à retrouver un état de sécurité et d'équilibre.
            </p>
          </div>
        </div>
      </section>

      <BreathLine className="w-full h-10 opacity-40" />

      {/* ============ SOLUTION ============ */}
      <section className="py-24" style={{ backgroundColor: C.surface }}>
        <div className="max-w-[900px] mx-auto px-8">
          <Eyebrow>Solution</Eyebrow>
          <Serif as="h2" className="mt-6 text-4xl md:text-5xl leading-tight mb-8">
            Rétablir <Serif italic style={{ color: C.sage }}>l'Équilibre</Serif>
          </Serif>

          <p className="text-lg mb-12" style={{ color: C.ink }}>
            La bonne nouvelle est que le système nerveux est <strong style={{ color: C.sageDeep }}>plastique</strong> : il peut être rééduqué et ré-entraîné. Les techniques de rééducation respiratoire agissent directement sur le système nerveux autonome pour restaurer l'équilibre entre le mode sympathique et parasympathique.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            <OrganicCard className="text-center" padding="p-8">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: C.sage }}>
                <Heart size={28} style={{ color: C.bg }} />
              </div>
              <Serif as="h4" className="text-lg mb-2" style={{ color: C.ink }}>Respiration Contrôlée</Serif>
              <p className="text-sm" style={{ color: C.ink2 }}>Active directement le nerf vague et le système parasympathique</p>
            </OrganicCard>

            <OrganicCard className="text-center" padding="p-8">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: C.sageDeep }}>
                <Activity size={28} style={{ color: C.bg }} />
              </div>
              <Serif as="h4" className="text-lg mb-2" style={{ color: C.ink }}>Conscience Corporelle</Serif>
              <p className="text-sm" style={{ color: C.ink2 }}>Permet de détecter et relâcher les tensions chroniques</p>
            </OrganicCard>

            <OrganicCard className="text-center" padding="p-8">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: C.ink }}>
                <Shield size={28} style={{ color: C.bg }} />
              </div>
              <Serif as="h4" className="text-lg mb-2" style={{ color: C.ink }}>Signal de Sécurité</Serif>
              <p className="text-sm" style={{ color: C.ink2 }}>Ré-apprend au système nerveux qu'il est en sécurité</p>
            </OrganicCard>
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="relative py-32 overflow-hidden" style={{ backgroundColor: C.ink, color: C.bg }}>
        <div
          aria-hidden
          className="absolute inset-0 opacity-30"
          style={{ background: `radial-gradient(circle at 70% 50%, ${C.sage}, transparent 60%)` }}
        />
        <div className="max-w-[1100px] mx-auto px-8 text-center relative">
          <Eyebrow>
            <span style={{ color: C.sage }}>Commencer</span>
          </Eyebrow>
          <Serif as="h2" className="mt-8 text-5xl md:text-6xl leading-[1.02]">
            Commencez Votre <Serif italic style={{ color: C.sage }}>Rééducation</Serif>
          </Serif>
          <p className="mt-8 text-lg max-w-xl mx-auto" style={{ color: 'rgba(249,248,246,0.7)' }}>
            Découvrez nos exercices conçus pour rétablir l'équilibre de votre système nerveux autonome et retrouver une respiration optimale.
          </p>
          <div className="mt-10">
            <Link
              to="/app-landing"
              className="inline-flex items-center gap-3 px-10 py-5 rounded-full text-base font-medium transition-all duration-300 hover:gap-4"
              style={{ backgroundColor: C.sage, color: C.ink, fontFamily: 'Figtree, sans-serif' }}
            >
              Accéder aux exercices
              <ChevronRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="py-14" style={{ backgroundColor: C.bg }}>
        <div className="max-w-[1320px] mx-auto px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <Serif as="div" className="text-2xl">Julien Jaccard</Serif>
            <p className="text-sm mt-1" style={{ color: C.ink2 }}>Physiothérapie - Rééducation respiratoire</p>
          </div>
          <div className="flex flex-col md:flex-row gap-x-10 gap-y-2 text-sm" style={{ color: C.ink2 }}>
            <a href="mailto:julienjaccard@mains-nuages.ch" style={{ color: C.sageDeep }} className="underline-offset-4 hover:underline">julienjaccard@mains-nuages.ch</a>
            <Link to="/contact" className="hover:opacity-70">Contact</Link>
            <Link to="/documents" className="hover:opacity-70">Documents</Link>
          </div>
          <p className="text-xs uppercase tracking-[0.25em]" style={{ color: C.sageDeep }}>
            © {new Date().getFullYear()} - Mains Nuages
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CausesPagePreviewV1;
